import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, ShieldAlert } from 'lucide-react';
import { supabase } from '../lib/supabase';

export const ForcePasswordChangePage: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword, user, signOut } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validatePassword = (password: string): string | null => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await updatePassword(password);

      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      if (user) {
        const { error: dbError } = await supabase
          .from('users')
          .update({ password_reset_required: false })
          .eq('id', user.id);

        if (dbError) {
          console.error('Failed to clear password reset flag:', dbError);
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError('An unexpected error occurred while updating your password');
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-md w-full border border-gray-200/50 dark:border-gray-700/50">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 rounded-full w-20 h-20 mx-auto mb-6 shadow-lg">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Password Updated!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Your password has been successfully updated. You can now access your account with your new password.
            </p>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-xl p-4 mb-6 border border-emerald-200 dark:border-emerald-800">
            <p className="text-emerald-700 dark:text-emerald-300 text-sm text-center">
              Redirecting you to the homepage in a few seconds...
            </p>
          </div>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary text-white px-6 py-3 rounded-xl font-semibold hover:from-theme-primary hover:to-theme-secondary transition-all duration-200 shadow-lg transform hover:scale-105"
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-md w-full border border-gray-200/50 dark:border-gray-700/50">
        <div className="text-center mb-6">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-4 rounded-2xl w-20 h-20 mx-auto mb-6 shadow-lg">
            <ShieldAlert className="h-12 w-12 text-white drop-shadow-sm" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-3">
            Password Change Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            For security reasons, you must change your default password before accessing your account.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
              <p className="text-red-700 dark:text-red-300 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white font-medium"
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {password && (
              <div className="mt-2 space-y-1">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Password requirements:</p>
                <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 ml-2">
                  <li className={`flex items-center space-x-1 ${password.length >= 6 ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                    <span>•</span>
                    <span>At least 6 characters</span>
                  </li>
                  <li className={`flex items-center space-x-1 ${/(?=.*[a-z])/.test(password) ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                    <span>•</span>
                    <span>One lowercase letter</span>
                  </li>
                  <li className={`flex items-center space-x-1 ${/(?=.*[A-Z])/.test(password) ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                    <span>•</span>
                    <span>One uppercase letter</span>
                  </li>
                  <li className={`flex items-center space-x-1 ${/(?=.*\d)/.test(password) ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                    <span>•</span>
                    <span>One number</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-gray-900 dark:text-white font-medium"
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {confirmPassword && (
              <div className="mt-2">
                {password === confirmPassword ? (
                  <div className="flex items-center space-x-1 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-xs font-medium">Passwords match</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-xs font-medium">Passwords do not match</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || password !== confirmPassword || !password || validatePassword(password) !== null}
            className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary text-white py-3 px-6 rounded-xl font-semibold hover:from-theme-primary hover:to-theme-secondary focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg shadow-theme-primary/25 transform hover:scale-105"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Updating Password...</span>
              </>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                <span>Update Password</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={handleSignOut}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium text-sm transition-colors duration-200"
          >
            Sign out instead
          </button>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
          <p className="text-amber-800 dark:text-amber-300 text-sm text-center">
            <strong>Security Notice:</strong> This is required because you were created with a default password. Please choose a strong, unique password.
          </p>
        </div>
        
        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4">
          Need help?{' '}
          <a 
            href="mailto:support@footypicks.co.uk"
            className="text-theme-primary hover:text-theme-primary/80 hover:underline font-medium transition-colors"
          >
            support@footypicks.co.uk
          </a>
        </p>
      </div>
    </div>
  );
};
